import { PaginationState, Updater } from '@tanstack/react-table';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PageInfo } from '@/@generated/codegen/API';

import { getPrefixedQueryParamKey } from './useQueryParams';

type MyPaginationState = {
  pageIndex: number;
  pageSize: number;
};

export const QUERY_PARAM_PAGINATION_AFTER_KEY = 'after';
export const QUERY_PARAM_PAGINATION_BEFORE_KEY = 'before';
export const QUERY_PARAM_PAGE_INDEX_KEY = 'pageIndex';

export function usePaginationState(pageSize: number, prefix: string | null) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationState, setPaginationState] = useState<MyPaginationState>({
    pageIndex: +(
      searchParams.get(getPrefixedQueryParamKey(QUERY_PARAM_PAGE_INDEX_KEY, prefix)) || 0
    ),
    pageSize: pageSize,
  });

  const switchPage = useCallback(
    (newState: MyPaginationState, pageInfo: PageInfo) => {
      const pageIndexDiff = newState.pageIndex - paginationState.pageIndex;

      if (pageIndexDiff === 0) return;
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        if (pageIndexDiff > 0) {
          // Moving to next page
          newParams.set(
            getPrefixedQueryParamKey(QUERY_PARAM_PAGINATION_AFTER_KEY, prefix),
            pageInfo.endCursor || ''
          );
          newParams.delete(getPrefixedQueryParamKey(QUERY_PARAM_PAGINATION_BEFORE_KEY, prefix));
        } else {
          // Moving to previous page
          newParams.set(
            getPrefixedQueryParamKey(QUERY_PARAM_PAGINATION_BEFORE_KEY, prefix),
            pageInfo.startCursor || ''
          );
          newParams.delete(getPrefixedQueryParamKey(QUERY_PARAM_PAGINATION_AFTER_KEY, prefix));
        }
        newParams.set(
          getPrefixedQueryParamKey(QUERY_PARAM_PAGE_INDEX_KEY, prefix),
          String(newState.pageIndex)
        );
        return newParams;
      });
      setPaginationState(newState);
    },
    [paginationState, setSearchParams, prefix]
  );

  const createOnPaginationChangeFunc = useCallback(
    (pageInfo: PageInfo) => (updater: Updater<PaginationState>) => {
      const newState = typeof updater === 'function' ? updater(paginationState) : updater;
      switchPage(newState, pageInfo);
    },
    [paginationState, switchPage]
  );

  useEffect(() => {
    setPaginationState((prev) => ({
      ...prev,
      pageIndex: +(
        searchParams.get(getPrefixedQueryParamKey(QUERY_PARAM_PAGE_INDEX_KEY, prefix)) || 0
      ),
    }));
  }, [searchParams.get(getPrefixedQueryParamKey(QUERY_PARAM_PAGE_INDEX_KEY, prefix)), prefix]);

  return {
    paginationState,
    createOnPaginationChangeFunc,
  };
}
